import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiLayers, FiExternalLink } = FiIcons;

// Colors for consistency with other views
const CLUSTER_COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', 
  '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
];

const ManageElements = () => {
  const [elements, setElements] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [elementClusters, setElementClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentElement, setCurrentElement] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', cluster_id: '' });
  
  // New state for tab navigation
  const [activeClusterId, setActiveClusterId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [elementsRes, clustersRes, relationsRes] = await Promise.all([
        apiService.getElements(),
        apiService.getClusters(),
        apiService.getClusterElementRelations()
      ]);

      if (elementsRes.status === 'success') setElements(elementsRes.data);
      
      let fetchedClusters = [];
      if (clustersRes.status === 'success') {
        fetchedClusters = clustersRes.data;
        setClusters(fetchedClusters);
      }
      
      let fetchedRelations = [];
      if (relationsRes.status === 'success') {
        fetchedRelations = relationsRes.data;
        setElementClusters(fetchedRelations);
      }
      
      // Initialize active cluster if not set
      if (activeClusterId === null && fetchedClusters.length > 0) {
        // We'll calculate groupings first to decided active tab, or just pick first cluster
        setActiveClusterId(fetchedClusters[0].id);
      } else if (activeClusterId === null) {
         setActiveClusterId('none');
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to group elements by cluster
  const getClusteredElements = () => {
    const grouped = {};
    const noCluster = [];

    // Initialize groups for all clusters
    clusters.forEach((c, index) => {
      grouped[c.id] = { 
        ...c, 
        elements: [],
        color: CLUSTER_COLORS[index % CLUSTER_COLORS.length]
      };
    });

    elements.forEach(element => {
      const relation = elementClusters.find(r => r.element_id === element.id);
      if (relation && grouped[relation.cluster_id]) {
        grouped[relation.cluster_id].elements.push(element);
      } else {
        noCluster.push(element);
      }
    });

    const result = Object.values(grouped); // Show all clusters even if empty
    
    // Always add "Nicht zugeordnet" tab
    result.push({
        id: 'none',
        title: 'Nicht zugeordnet',
        description: 'Elemente ohne Cluster-Zuordnung',
        elements: noCluster,
        color: '#6b7280'
    });
    
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let elementId;
      if (currentElement) {
        await apiService.updateElement(currentElement.id, {
          title: formData.title,
          description: formData.description
        });
        elementId = currentElement.id;
      } else {
        const res = await apiService.createElement({
          title: formData.title,
          description: formData.description
        });
        elementId = res.id;
      }

      // Handle Cluster Assignment
      const existingRelation = elementClusters.find(r => r.element_id === elementId);
      
      if (formData.cluster_id) {
        if (existingRelation) {
          // Update existing relation if changed
          if (existingRelation.cluster_id !== parseInt(formData.cluster_id)) {
             await apiService.updateClusterElementRelation(existingRelation.id, {
               cluster_id: parseInt(formData.cluster_id),
               element_id: elementId
             });
          }
        } else {
          // Create new relation
          await apiService.createClusterElementRelation({
            cluster_id: parseInt(formData.cluster_id),
            element_id: elementId
          });
        }
      } else if (existingRelation) {
        // If cluster_id is empty but relation exists, delete it (unassign)
        await apiService.deleteClusterElementRelation(existingRelation.id);
      }

      fetchData();
      closeModal();
    } catch (error) {
      console.error('Error saving element:', error);
      alert('Fehler beim Speichern des Elements');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie dieses Element wirklich löschen?')) {
      try {
        const relation = elementClusters.find(r => r.element_id === id);
        if (relation) {
          await apiService.deleteClusterElementRelation(relation.id);
        }
        
        await apiService.deleteElement(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting element:', error);
        alert('Fehler beim Löschen des Elements');
      }
    }
  };

  const openModal = (element = null) => {
    if (element) {
      setCurrentElement(element);
      const relation = elementClusters.find(r => r.element_id === element.id);
      setFormData({ 
        title: element.title, 
        description: element.description,
        cluster_id: relation ? relation.cluster_id : (activeClusterId !== 'none' ? activeClusterId : '')
      });
    } else {
      setCurrentElement(null);
      // Pre-select active cluster if valid
      setFormData({ 
          title: '', 
          description: '', 
          cluster_id: (activeClusterId && activeClusterId !== 'none') ? activeClusterId : '' 
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentElement(null);
    setFormData({ title: '', description: '', cluster_id: '' });
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
    </div>
  );

  const clusteredElements = getClusteredElements();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Elemente verwalten</h2>
        <div className="flex gap-2">
          <a
            href="#/public/elements"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors border border-gray-300"
          >
            <SafeIcon icon={FiExternalLink} className="mr-2" /> Öffentliche Seite
          </a>
          <button
            onClick={() => openModal()}
            className="flex items-center px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark transition-colors"
          >
            <SafeIcon icon={FiPlus} className="mr-2" /> Neues Element
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-6">
          {/* Tab Navigation */}
          <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
             {clusteredElements.map(cluster => (
                <button
                   key={cluster.id}
                   onClick={() => setActiveClusterId(cluster.id)}
                   className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border ${
                      activeClusterId === cluster.id
                        ? 'bg-[#152627] text-white border-[#152627] shadow-md'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                   }`}
                >
                   {cluster.title}
                </button>
             ))}
          </div>

          {/* Active Cluster Content */}
          {clusteredElements.map((cluster) => {
             if (cluster.id !== activeClusterId) return null;

             return (
               <div 
                 key={cluster.id} 
                 className="rounded-lg border overflow-hidden animate-fadeIn"
                 style={{
                    backgroundColor: cluster.id === 'none' ? '#f9fafb' : `${cluster.color}08`, 
                    borderColor: cluster.id === 'none' ? '#e5e7eb' : `${cluster.color}30`
                 }}
               >
                 <div 
                   className="px-4 py-3 border-b flex justify-between items-center"
                   style={{ 
                     borderColor: cluster.id === 'none' ? '#e5e7eb' : `${cluster.color}30`,
                     backgroundColor: cluster.id === 'none' ? '#f3f4f6' : `${cluster.color}15`
                   }}
                 >
                    <div className="flex items-center gap-2">
                        <SafeIcon icon={FiLayers} className="w-5 h-5" style={{ color: cluster.id === 'none' ? '#374151' : cluster.color }} />
                        <h3 className="font-bold text-lg" style={{ color: cluster.id === 'none' ? '#374151' : cluster.color }}>
                           {cluster.title}
                        </h3>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded bg-white bg-opacity-50 text-gray-600">
                       {cluster.elements.length} Elemente
                    </span>
                 </div>

                 <div className="divide-y divide-gray-200 bg-white">
                    {cluster.elements.length === 0 ? (
                      <div className="px-6 py-12 text-center text-gray-400 italic">
                        Keine Elemente in diesem Cluster
                      </div>
                    ) : (
                      cluster.elements.map((element) => (
                        <div key={element.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div>
                            <h4 className="text-lg font-medium text-brand-dark">{element.title}</h4>
                            <p className="text-sm text-gray-500">{element.description}</p>
                          </div>
                          <div className="flex space-x-3">
                            <button
                              onClick={() => openModal(element)}
                              className="text-brand-secondary hover:text-brand-dark p-2 rounded-full hover:bg-brand-secondary/10 transition-colors"
                              title="Bearbeiten"
                            >
                              <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(element.id)}
                              className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                              title="Löschen"
                            >
                              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                 </div>
               </div>
             );
          })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">
                {currentElement ? 'Element bearbeiten' : 'Neues Element'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cluster zuordnen</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  value={formData.cluster_id}
                  onChange={(e) => setFormData({ ...formData, cluster_id: e.target.value })}
                >
                  <option value="">Kein Cluster</option>
                  {clusters.map(cluster => (
                    <option key={cluster.id} value={cluster.id}>
                      {cluster.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Ordnen Sie dieses Element optional einem Cluster zu.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageElements;